import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { fontRoboto, transientOptions } from "@product/scmp-sdk";

import {
  ArticleImageContainer,
  DescriptionContainer as ArticleImageDescriptionContainer,
} from "scmp-app/components/article/article-image/styles";
import { ArticleLandingBanner } from "scmp-app/components/article/article-landing-banner";
import { pageStyle } from "scmp-app/components/article/article-render/article-content/standard-styles";
import { ContentSubHeadlineContainer } from "scmp-app/components/content/content-sub-headline/styles";
import { ContentSummaryContainer } from "scmp-app/components/content/content-summary/styles";
import {
  StyledFooter as BlockQuoteFooter,
  StyledQuoteIcon as BlockQuoteIcon,
} from "scmp-app/components/schema-render/common/blockquote-quote/styles";
import { StyledParagraph } from "scmp-app/components/schema-render/common/paragraph/styles";
import {
  Caption as TableCaption,
  StyledSummary as TableSummary,
} from "scmp-app/components/schema-render/common/table/styles";
import { StyledTh, StyledTr } from "scmp-app/components/schema-render/content/styles";
import { VideoTitle } from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-footer/styles";
import { cssVariableValues, setFullViewportWidthStyles } from "scmp-app/lib/styles";
import type { WithStickyStackedHeightProps } from "scmp-app/lib/styles";

export const PhotoEssayArticleContainer = styled.div`
  padding-inline: 20px;

  background: #1c1e22;

  ${setFullViewportWidthStyles()}
`;

export const PhotoEssayArticleWrapper = styled.div`
  display: grid;
  grid:
    "landing-banner landing-banner landing-banner" min-content
    "follow follow follow" min-content
    "summary-and-author summary-and-author summary-and-author" min-content
    "speech-player speech-player speech-player" min-content
    "header-ad header-ad header-ad" min-content
    "content content content" min-content
    "paywall paywall paywall" min-content
    "content-footer-ad content-footer-ad content-footer-ad" min-content
    "comment comment social" min-content
    "more-on-this more-on-this more-on-this" min-content
    "author-detail author-detail author-detail" min-content
    "topic topic topic" min-content
    "footer-ad footer-ad footer-ad" min-content
    / minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);

  inline-size: 100%;
  max-inline-size: 1456px;
  margin-inline: auto;

  ${props => props.theme.breakpoints.up("tablet")} {
    grid:
      "landing-banner landing-banner landing-banner landing-banner" min-content
      "left . summary-and-author summary-and-author" min-content
      "left . speech-player speech-player" min-content
      "left . header-ad header-ad" min-content
      "left . content content" min-content
      "left . paywall paywall" min-content
      "left . content-footer-ad content-footer-ad" min-content
      "left . comment social" min-content
      "left . more-on-this more-on-this" min-content
      "left . author-detail author-detail" min-content
      "left . topic topic" min-content
      "left . footer-ad footer-ad" min-content
      "left . . ." min-content
      / 60px 30px minmax(0, 0.6fr) minmax(0, 0.4fr);
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    grid:
      "landing-banner landing-banner landing-banner landing-banner" min-content
      "left summary-and-author summary-and-author ." min-content
      "left speech-player speech-player ." min-content
      "left header-ad header-ad ." min-content
      "left content content ." min-content
      "left paywall paywall ." min-content
      "left content-footer-ad content-footer-ad ." min-content
      "left comment social ." min-content
      "left more-on-this more-on-this ." min-content
      "left author-detail author-detail ." min-content
      "left topic topic ." min-content
      "left . . ." min-content
      / minmax(0, 1fr) minmax(0, 400px) minmax(0, 280px) minmax(0, 1fr);
  }

  > *:empty {
    margin: 0;
    padding: 0;
  }
`;

type LandingBannerProps = {
  $addAppBarNegativeMargin: boolean;
};

export const LandingBanner = styled(ArticleLandingBanner, {
  ...transientOptions,
})<LandingBannerProps>`
  grid-area: landing-banner;

  ${props => props.theme.breakpoints.only("mobile")} {
    ${setFullViewportWidthStyles()}
  }

  ${props =>
    props.$addAppBarNegativeMargin
      ? css`
          margin-block-start: calc(${cssVariableValues.appBarHeight} * -1);
        `
      : css`
          margin-block-start: -40px;
        `}
`;

export const Left = styled.div<WithStickyStackedHeightProps>`
  grid-area: left;

  position: sticky;
  inset-block-start: ${props => props.$stickyStackedHeight + 40}px;

  display: none;

  transition: inset-block-start 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: flex;
    flex-flow: column nowrap;
    gap: 60px;
    align-items: center;
    align-self: flex-start;

    margin-block-start: 40px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    padding-inline-end: 88px;
  }
`;

export const SummaryAndAuthor = styled.div`
  grid-area: summary-and-author;

  padding-block: 32px;

  ${ContentSubHeadlineContainer}, ${ContentSummaryContainer} {
    margin-block-end: 32px;

    color: #ffffff;
    font-weight: 300;
    font-size: 24px;
    font-family: ${fontRoboto};
    line-height: 32px;

    & ul {
      padding: 0;

      list-style: disc;

      li {
        margin-block-end: 12px;

        ${props => props.theme.breakpoints.only("mobile")} {
          margin-inline-start: 20px;
        }
      }
    }

    & li:before {
      display: none;
    }

    ${StyledParagraph} {
      display: list-item;
    }

    ${props => props.theme.breakpoints.only("mobile")} {
      font-size: 19px;
      line-height: 23px;
    }
  }

  ${props => props.theme.breakpoints.only("mobile")} {
    margin-block-start: calc(${cssVariableValues.appBarHeight} * -1);
    padding-block: 20px 28px;
  }
`;

export type ContentProps = {
  $variant?: "light" | "dark";
};

export const Content = styled.div<ContentProps>`
  grid-area: content;

  margin-block-start: 32px;

  ${ArticleImageDescriptionContainer} {
    ${props => props.theme.breakpoints.up("tablet")} {
      padding-inline: 0;
    }
  }

  ${ArticleImageDescriptionContainer}, ${TableCaption}, ${VideoTitle} {
    color: #d7d7d7;
  }

  ${TableSummary}, ${StyledTh} {
    color: #ffffff;
  }

  ${BlockQuoteIcon} path, ${BlockQuoteFooter} path {
    fill: #ffffff;
  }

  ${BlockQuoteFooter} {
    border-color: #ffffff;
  }

  ${StyledTr} {
    &:nth-child(2n) {
      color: #000000;
    }
  }

  ${props => pageStyle(props)}

  ${props => props.theme.breakpoints.up("desktop")} {
    ${ArticleImageContainer} {
      margin-inline: -88px;
    }
  }

  ${ArticleImageDescriptionContainer} {
    line-height: 16px;
  }
`;

export const CommentAndReaction = styled.div`
  grid-area: comment;

  margin-block-start: 52px;

  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 335px;
  }
`;

export const Social = styled.div`
  grid-area: social;

  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;

  > *:empty {
    margin: 0;
    padding: 0;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 335px;
  }
`;

export const SocialMobile = styled.div`
  display: none;

  margin-block-start: 52px;

  ${props => props.theme.breakpoints.only("mobile")} {
    display: block;
  }
`;

export const SocialDesktop = styled.div`
  display: none;

  margin-block-start: 52px;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: block;
  }
`;

export const AuthorDetail = styled.div`
  grid-area: author-detail;

  margin-block-start: 32px;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 20px;
  }
`;

export const Topic = styled.div`
  grid-area: topic;

  margin-block-start: 20px;
  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 16px;
  }
`;

export const Follow = styled.div`
  margin-block-end: 32px;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: none;
  }
`;

export const HeaderAdSlot = styled.div`
  grid-area: header-ad;
`;

export const ContentFooterAdSlot = styled.div`
  grid-area: content-footer-ad;
`;

export const FooterAdSlot = styled.div`
  grid-area: footer-ad;

  display: none;

  margin-block-start: 20px;

  ${props => props.theme.breakpoints.down("desktop")} {
    display: block;
  }
`;

export const SpeechPlayer = styled.div`
  grid-area: speech-player;
`;

export const Paywall = styled.div`
  grid-area: paywall;
`;
